import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  type ModalMotionHiddenEndEventDetail,
  PHeading,
  type PHeadingProps,
  PModal,
  type PModalProps,
} from '@porsche-design-system/components-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { ButtonGroup } from './ButtonGroup';

const StyledModal = styled(PModal, {
  base: {
    '--p-modal-width': 'clamp(600px, 70vw, 800px)',
  },

  variants: {
    size: {
      small: {
        '--p-modal-width': 'clamp(400px, 30vw, 500px)',
      },
      large: {
        '--p-modal-width': 'clamp(600px, 70vw, 1150px)',
      },
      max: {
        '--p-modal-width': 'clamp(600px, 70vw, 1500px)',
      },
      initial: {
        '--p-modal-width': 'var(--p-modal-width)',
      },
    },
  },
});

export const Modal = ({
  children,
  open,
  onMotionHiddenEnd,
  ...props
}: {
  size?: 'small' | 'large' | 'max' | 'initial';
  children: React.ReactNode;
} & PModalProps) => {
  const [remainOpen, setRemainOpen] = useState(open);

  useEffect(() => {
    if (open) {
      // Necessary for conditional rendering, so that the content is not immediately hidden when the modal is closed.
      setRemainOpen(true);
    }

    // The PDS removes the scroll lock after the modal has been closed.
    // However, as several modals may be open, the scroll lock can only be removed when no modal is open.
    if (document.querySelectorAll('p-modal[aria-hidden=false]').length > 0) {
      document.body.classList.add('scroll-lock');
    } else {
      document.body.classList.remove('scroll-lock');
    }

    return () => {
      // Cleanup scroll lock if component completely removed from DOM
      if (
        document.querySelectorAll('p-modal[aria-hidden=false]').length === 0
      ) {
        document.body.classList.remove('scroll-lock');
      }
    };
  }, [open]);

  const showContent = open || remainOpen;

  return createPortal(
    <StyledModal
      aria-modal="true"
      aria-hidden={open ? 'false' : 'true'}
      fullscreen={{ base: true, s: false }}
      open={open}
      onMotionHiddenEnd={(
        event: CustomEvent<ModalMotionHiddenEndEventDetail>,
      ) => {
        onMotionHiddenEnd?.(event);

        // Cleanup after modal is closed.
        setRemainOpen(false);
      }}
      {...props}
    >
      {showContent ? children : null}
    </StyledModal>,
    document.body,
  );
};

const Header = ({ children, ...props }: PHeadingProps) => (
  <PHeading slot="header" size="lg" tag="h2" {...props}>
    {children}
  </PHeading>
);
Modal.Header = Header;

const Footer = ({
  sticky = false,
  children,
}: {
  sticky?: boolean;
  children: React.ReactNode;
}) => (
  <ButtonGroup slot={sticky ? 'footer' : undefined}>{children}</ButtonGroup>
);
Modal.Footer = Footer;
